import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import { HiOutlineCheckCircle, HiOutlineXCircle } from "react-icons/hi";
import { MdOutlinePendingActions } from "react-icons/md";
import Layout from "../Layout";
import Modal from "../components/Modals/Modal";
import { Button, Input } from "../components/Form";
import ConfirmationAlert from "../components/ConfirmationAlert";
import TwoColumRow from "../components/TwoColumRow";
import {
  useAprobarSolicitud,
  useRechazarSolicitud,
  useSolicitudesCita,
} from "../customHooks/queries/useSolicitudesCita";

// Cola de reservas hechas por el paciente vía Cal.com (/reservar), pendientes de que recepción las
// revise. Aprobar crea la Cita real (y dispara el recordatorio de WhatsApp); rechazar simplemente
// la descarta. Ver SolicitudCitaController en el backend.

function formatFecha(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("es-BO", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Separa "Juan Pérez" en nombre/apellidos lo mejor posible, para precargar el form de paciente
// nuevo — recepción igual puede corregirlo antes de confirmar.
function splitNombre(nombreCompleto) {
  const partes = (nombreCompleto || "").trim().split(/\s+/);
  if (partes.length <= 1) return { nombre: partes[0] || "", apellidos: "" };
  const mitad = Math.ceil(partes.length / 2);
  return {
    nombre: partes.slice(0, mitad).join(" "),
    apellidos: partes.slice(mitad).join(" "),
  };
}

function SolicitudCard({ solicitud, onAprobarDirecto, onAprobarNuevoPaciente, onRechazar }) {
  const pacienteExistente = solicitud.patient;

  const handleRechazar = () => {
    ConfirmationAlert({
      title: "Rechazar solicitud",
      text: `¿Seguro que querés rechazar la reserva de ${solicitud.nombre_paciente}?`,
      onAccept: () => onRechazar(solicitud.id),
    });
  };

  const handleAprobar = () => {
    if (pacienteExistente) {
      ConfirmationAlert({
        title: "Aprobar solicitud",
        text: `Se va a crear la cita de ${solicitud.nombre_paciente} con ${
          pacienteExistente.persona?.nombre || ""
        } ${pacienteExistente.persona?.apellidos || ""} (paciente ya registrado).`,
        onAccept: () => onAprobarDirecto(solicitud.id),
      });
      return;
    }
    onAprobarNuevoPaciente(solicitud);
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 flex flex-col gap-3 border border-border">
      <div className="flex justify-between items-start gap-3">
        <div>
          <h3 className="font-bold text-md">{solicitud.nombre_paciente}</h3>
          <p className="text-xs text-textGray">
            {solicitud.telefono ? `📞 ${solicitud.telefono}` : "📞 sin teléfono"}
            {solicitud.email ? ` · ${solicitud.email}` : ""}
          </p>
        </div>
        {pacienteExistente ? (
          <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full whitespace-nowrap">
            Paciente existente
          </span>
        ) : (
          <span className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full whitespace-nowrap">
            Paciente nuevo
          </span>
        )}
      </div>

      <div className="text-sm">
        <p>
          <span className="font-semibold">Cuándo: </span>
          {formatFecha(solicitud.fecha_hora_inicio)}
        </p>
        <p>
          <span className="font-semibold">Tipo: </span>
          {solicitud.tipo_evento || "-"}
        </p>
        {solicitud.motivo && (
          <p>
            <span className="font-semibold">Motivo: </span>
            {solicitud.motivo}
          </p>
        )}
        {pacienteExistente && (
          <p className="text-subMain">
            Sugerido: {pacienteExistente.persona?.nombre}{" "}
            {pacienteExistente.persona?.apellidos}
          </p>
        )}
      </div>

      <div className="flex gap-3 mt-2">
        <Button label="Rechazar" Icon={HiOutlineXCircle} onClick={handleRechazar} />
        <Button label="Aprobar" Icon={HiOutlineCheckCircle} onClick={handleAprobar} />
      </div>
    </div>
  );
}

function NuevoPacienteModal({ solicitud, onClose, onConfirm, loading }) {
  const { nombre, apellidos } = splitNombre(solicitud?.nombre_paciente);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { nombre, apellidos } });

  const onSubmit = (data) => {
    // CI de marcador de posición (mismo patrón que "Registro rápido de paciente" en Notificaciones)
    // — recepción lo puede corregir después desde la ficha del paciente si consigue el CI real.
    onConfirm({ ...data, ci: uuidv4() });
  };

  return (
    <Modal
      title="Confirmar paciente nuevo"
      isOpen={!!solicitud}
      closeModal={onClose}
      width={"max-w-xl"}
    >
      <div className="flex flex-colo gap-5">
        <p className="text-sm text-textGray text-center">
          Esta reserva no coincide con ningún paciente registrado. Confirmá los
          datos antes de crear la cita.
        </p>
        <TwoColumRow>
          <Input
            label="Nombres"
            color={true}
            type="text"
            register={register("nombre", { required: "Este campo es requerido" })}
            error={errors.nombre}
          />
          <Input
            label="Apellidos"
            color={true}
            type="text"
            register={register("apellidos", { required: "Este campo es requerido" })}
            error={errors.apellidos}
          />
        </TwoColumRow>
        <Button
          label="Crear paciente y aprobar"
          Icon={HiOutlineCheckCircle}
          loading={loading}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </Modal>
  );
}

function SolicitudesCita() {
  const { data: solicitudes, isLoading } = useSolicitudesCita();
  const aprobar = useAprobarSolicitud();
  const rechazar = useRechazarSolicitud();
  const [solicitudParaPaciente, setSolicitudParaPaciente] = useState(null);

  const handleAprobarDirecto = (id) => {
    aprobar.mutate(
      { id },
      {
        onSuccess: () => toast.success("Cita creada correctamente."),
        onError: (e) =>
          toast.error(
            "No se pudo aprobar: " + (e?.response?.data?.error || e.message)
          ),
      }
    );
  };

  const handleConfirmarNuevoPaciente = (datos) => {
    aprobar.mutate(
      { id: solicitudParaPaciente.id, ...datos },
      {
        onSuccess: () => {
          toast.success("Paciente y cita creados correctamente.");
          setSolicitudParaPaciente(null);
        },
        onError: (e) =>
          toast.error(
            "No se pudo aprobar: " + (e?.response?.data?.error || e.message)
          ),
      }
    );
  };

  const handleRechazar = (id) => {
    rechazar.mutate(id, {
      onSuccess: () => toast.success("Solicitud rechazada."),
      onError: (e) =>
        toast.error(
          "No se pudo rechazar: " + (e?.response?.data?.error || e.message)
        ),
    });
  };

  return (
    <Layout title="Solicitudes de cita">
      <div className="my-6">
        {isLoading && <p className="text-center text-textGray">Cargando...</p>}
        {!isLoading && (!solicitudes || solicitudes.length === 0) && (
          <div className="flex-colo gap-3 py-20 text-textGray">
            <MdOutlinePendingActions className="text-5xl" />
            <p>No hay solicitudes pendientes.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {solicitudes?.map((s) => (
            <SolicitudCard
              key={s.id}
              solicitud={s}
              onAprobarDirecto={handleAprobarDirecto}
              onAprobarNuevoPaciente={setSolicitudParaPaciente}
              onRechazar={handleRechazar}
            />
          ))}
        </div>
      </div>

      <NuevoPacienteModal
        solicitud={solicitudParaPaciente}
        onClose={() => setSolicitudParaPaciente(null)}
        onConfirm={handleConfirmarNuevoPaciente}
        loading={aprobar.isPending}
      />
    </Layout>
  );
}

export default SolicitudesCita;
